import { prefersReducedMotion } from "@theme/utilities";
import { ensureGsap } from "@theme/vp-motion";
import { getIntersectionRoot } from "@theme/scroll-container";
//#region assets/vp-order-steps.ts
/**
* Order-steps timeline: continuous vertical line draw + staggered step reveal.
*
* Markup:
*   <vp-order-steps>
*     <span data-order-steps-line></span>
*     <span data-order-steps-marker></span>  (per step)
*     [data-order-steps-content]             (per step)
*   </vp-order-steps>
*/
var VpOrderSteps = class extends HTMLElement {
	/** @type {IntersectionObserver | null} */
	#observer = null;
	/** @type {ResizeObserver | null} */
	#resizeObserver = null;
	#played = false;
	/** @type {typeof window.gsap | null} */
	#gsap = null;
	connectedCallback() {
		this.#init();
	}
	disconnectedCallback() {
		this.#observer?.disconnect();
		this.#observer = null;
		this.#resizeObserver?.disconnect();
		this.#resizeObserver = null;
	}
	async #init() {
		const line = this.querySelector("[data-order-steps-line]");
		const markers = Array.from(this.querySelectorAll("[data-order-steps-marker]"));
		const contents = Array.from(this.querySelectorAll("[data-order-steps-content]"));
		const header = this.querySelector("[data-order-steps-header]");
		const actions = this.querySelector("[data-order-steps-actions]");
		if (!line || !markers.length) return;
		this.#positionLine(line, markers);
		const reducedMotion = prefersReducedMotion();
		const designMode = document.documentElement.classList.contains("shopify-design-mode");
		if (reducedMotion || designMode) {
			this.setAttribute("data-order-steps-played", "");
			line.style.transform = "";
			return;
		}
		this.#gsap = await ensureGsap();
		if (!this.#gsap) {
			this.setAttribute("data-order-steps-played", "");
			line.style.transform = "";
			return;
		}
		const gsap = this.#gsap;
		gsap.set(line, {
			scaleY: 0,
			transformOrigin: "top center"
		});
		gsap.set(markers, {
			scale: 0,
			transformOrigin: "center center"
		});
		gsap.set(contents, {
			autoAlpha: 0,
			y: 12
		});
		if (header) gsap.set(header, {
			autoAlpha: 0,
			y: 16
		});
		if (actions) gsap.set(actions, {
			autoAlpha: 0,
			y: 16
		});
		this.#resizeObserver = new ResizeObserver(() => {
			this.#positionLine(line, markers);
		});
		this.#resizeObserver.observe(this);
		this.#observer = new IntersectionObserver((entries) => {
			if (!entries[entries.length - 1]?.isIntersecting || this.#played) return;
			this.#played = true;
			this.#animate({
				gsap,
				line,
				markers,
				contents,
				header,
				actions
			});
			this.#observer?.disconnect();
			this.#observer = null;
		}, {
			root: getIntersectionRoot(),
			rootMargin: "0px 0px -8% 0px",
			threshold: .15
		});
		this.#observer.observe(this);
	}
	/**
	* @param {Element} line
	* @param {Element[]} markers
	*/
	#positionLine(line, markers) {
		const wrap = this.querySelector("[data-order-steps-timeline]");
		const first = markers[0];
		const last = markers[markers.length - 1];
		if (!(wrap instanceof HTMLElement) || !(first instanceof HTMLElement) || !(last instanceof HTMLElement)) return;
		const wrapRect = wrap.getBoundingClientRect();
		const firstRect = first.getBoundingClientRect();
		const lastRect = last.getBoundingClientRect();
		const top = firstRect.top + firstRect.height / 2 - wrapRect.top;
		const bottom = wrapRect.bottom - (lastRect.top + lastRect.height / 2);
		line.style.top = `${top}px`;
		line.style.bottom = `${bottom}px`;
	}
	/**
	* @param {{
	*   gsap: typeof window.gsap,
	*   line: Element,
	*   markers: Element[],
	*   contents: Element[],
	*   header: Element | null,
	*   actions: Element | null
	* }} params
	*/
	#animate({ gsap, line, markers, contents, header, actions }) {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
		if (header) tl.to(header, {
			autoAlpha: 1,
			y: 0,
			duration: .5
		}, 0);
		tl.to(line, {
			scaleY: 1,
			duration: .9,
			ease: "power2.inOut"
		}, header ? .15 : 0);
		tl.to(markers, {
			scale: 1,
			duration: .35,
			stagger: .12,
			ease: "back.out(2)"
		}, .2);
		tl.to(contents, {
			autoAlpha: 1,
			y: 0,
			duration: .45,
			stagger: .12
		}, .25);
		if (actions) tl.to(actions, {
			autoAlpha: 1,
			y: 0,
			duration: .5
		}, "-=0.2");
		tl.eventCallback("onComplete", () => {
			this.setAttribute("data-order-steps-played", "");
			gsap.set([
				line,
				...markers,
				...contents,
				header,
				actions
			].filter(Boolean), { clearProps: "transform,opacity,visibility" });
		});
	}
};
if (!customElements.get("vp-order-steps")) customElements.define("vp-order-steps", VpOrderSteps);
//#endregion
